/**
 * The share modal. One instance, mounted once in `Layout`, driven by
 * `useShareStore` — so the homepage banner and the footer link (and any future
 * "share this guide" button) all open the same modal instead of each owning a
 * copy of this logic.
 *
 * Platform tiles select a platform and update the preview; they do not share
 * anything by themselves. Copy Link and native share are instant actions —
 * there is nothing to preview or edit first. The final "Share" button performs
 * the action for whichever platform is currently selected.
 */

import { useEffect, useState } from 'react'
import { Check, Copy, Share2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { TextArea } from '@/components/editor/fields'
import { useShareStore } from '@/store/shareStore'
import { track } from '@/lib/analytics'
import { SHARE_PLATFORMS, previewText, shareBody, shareHref, type SharePlatform } from '@/lib/share'
import { BrandIcon } from './BrandIcons'

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Clipboard API unavailable or blocked (older browser, insecure context).
    // The execCommand fallback is deprecated but still works everywhere that
    // the Clipboard API does not, which is exactly the gap it needs to cover.
    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(textarea)
      return ok
    } catch {
      return false
    }
  }
}

export const ShareModal = () => {
  const open = useShareStore((s) => s.open)
  const resource = useShareStore((s) => s.resource)
  const closeShare = useShareStore((s) => s.closeShare)

  const [platform, setPlatform] = useState<SharePlatform>('whatsapp')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)

  // Fired from here rather than from each trigger, so every future entry
  // point (a per-guide "share this guide" button included) reports the same
  // event without having to remember to call `track` itself.
  useEffect(() => {
    if (open) track('share_open')
  }, [open])

  const reset = () => {
    setMessage('')
    setStatus('')
    setLinkCopied(false)
    setPlatform('whatsapp')
  }

  const handleClose = () => {
    closeShare()
    reset()
  }

  const openPopup = (href: string) => {
    const win = window.open(href, '_blank', 'noopener,noreferrer,width=600,height=640')
    if (!win) {
      setStatus('⚠️ Unable to open the share window. Try Copy Link instead.')
      return false
    }
    return true
  }

  const handleCopyLink = async () => {
    const ok = await copyText(resource.url)
    if (ok) {
      setLinkCopied(true)
      setStatus('✅ Link copied!')
      track('share_copy_link')
      window.setTimeout(() => setLinkCopied(false), 1800)
    } else {
      setStatus('⚠️ Could not copy automatically — select and copy the link instead.')
    }
  }

  const handleNativeShare = async () => {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: resource.title,
          text: shareBody('native', resource, message),
          url: resource.url,
        })
        track('share_completed')
      } catch {
        // User cancelled the share sheet, or the platform rejected it — not an error worth surfacing.
      }
      return
    }
    // No Web Share API (most desktop browsers): copy link as the fallback.
    await handleCopyLink()
  }

  const handleShare = () => {
    if (platform === 'linkedin') {
      // LinkedIn's share dialog no longer accepts pre-filled body text, so the
      // caption is copied to the clipboard as a courtesy alongside opening it.
      void copyText(shareBody('linkedin', resource, message))
      const opened = openPopup(shareHref('linkedin', resource, message))
      if (opened) {
        setStatus('📋 Caption copied — paste it into your LinkedIn post.')
        track('share_completed')
      }
      return
    }
    const opened = openPopup(shareHref(platform, resource, message))
    if (opened) {
      setStatus('')
      track('share_completed')
    }
  }

  const selectPlatform = (id: SharePlatform) => {
    setPlatform(id)
    setStatus('')
    track('share_platform_selected')
  }

  const selectedLabel = SHARE_PLATFORMS.find((p) => p.id === platform)?.label ?? 'link'

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="🚀 Share this resource"
      labelledBy="share-modal-title"
      footer={
        <button type="button" onClick={handleShare} className="btn btn-primary min-h-11 gap-1.5">
          🚀 Share to {selectedLabel}
        </button>
      }
    >
      <p className="text-sm opacity-80">💙 Help someone else with their job search.</p>

      {/*
        Always five equal columns, never a 3-then-2 wrap. Emoji glyphs used to
        stand in for these — WhatsApp's circle, LinkedIn's briefcase and the
        raw 𝕏 character all render at different intrinsic sizes across
        platforms, which is what actually made the row look misaligned. Real
        brand marks in a fixed-size box fix that at the source; equal columns
        keep the row from breaking unevenly on narrow screens too.
      */}
      <div role="group" aria-label="Choose a platform" className="mt-4 grid grid-cols-5 gap-1.5 sm:gap-2">
        {SHARE_PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={platform === p.id}
            aria-label={`Share on ${p.label}`}
            onClick={() => selectPlatform(p.id)}
            className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-box border px-0.5 py-2 text-[11px] font-medium leading-tight transition-colors sm:text-xs ${
              platform === p.id ? 'border-primary bg-primary/10' : 'border-base-300 hover:border-primary'
            }`}
          >
            <BrandIcon platform={p.id} size={22} />
            <span className={platform === p.id ? 'text-primary' : ''}>{p.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={handleCopyLink}
          className="btn btn-sm min-h-11 flex-1 gap-1.5"
        >
          {linkCopied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {linkCopied ? 'Copied!' : 'Copy Link'}
        </button>
        <button
          type="button"
          onClick={handleNativeShare}
          className="btn btn-sm min-h-11 flex-1 gap-1.5"
        >
          <Share2 size={14} aria-hidden="true" />
          More ways to share
        </button>
      </div>

      <div className="mt-4">
        <TextArea
          label="✍️ Add your own message (optional)"
          placeholder="e.g. I found this useful for ATS resumes. Check it out!"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          maxLength={300}
        />
      </div>

      <div className="mt-4">
        <p className="mb-1 text-sm font-medium">👀 Preview</p>
        <div className="whitespace-pre-wrap rounded-box border border-base-300 bg-base-200 p-3 text-sm">
          {previewText(platform, resource, message)}
        </div>
      </div>

      {status && (
        <p role="status" className="mt-3 text-sm opacity-80">
          {status}
        </p>
      )}
    </Modal>
  )
}
